import * as XLSX from 'xlsx';
import type { ContactPoint, ContactPointKind } from '../../address-book/contactPoints';

export type ParsedContactsImportRow = {
  recordId: string;
  sourceName: string;
  clientName: string;
  contactPerson?: string;
  clientAddress?: string;
  clientNotes?: string;
  contactPoints: ContactPoint[];
};

export type ParseContactsSpreadsheetResult = {
  sheetName: string;
  rows: ParsedContactsImportRow[];
  skippedEmpty: number;
};

function normalizeHeader(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function cellValue(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = row[key];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
}

function splitMultiline(value: string): string[] {
  if (!value.trim()) return [];
  return value
    .split(/\r?\n|[;,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function parseEmailToken(token: string): { value: string; label: string } | null {
  const trimmed = token.trim();
  if (!trimmed) return null;

  const angle = trimmed.match(/^(.+?)\s*<([^>]+)>\s*$/);
  if (angle) {
    const value = angle[2].trim();
    const label = angle[1].trim().replace(/^["']|["']$/g, '') || 'Work';
    return value.includes('@') ? { value, label } : null;
  }

  const emailWithLabel = trimmed.match(/^(\S+@\S+)\s*(?:\((.+)\))?$/);
  if (emailWithLabel) {
    return {
      value: emailWithLabel[1].trim(),
      label: emailWithLabel[2]?.trim() || 'Work',
    };
  }

  if (trimmed.includes('@')) return { value: trimmed, label: 'Work' };
  return null;
}

function pushContactPoint(
  points: ContactPoint[],
  kind: ContactPointKind,
  value: string,
  label: string,
  isPrimary: boolean,
): void {
  points.push({
    kind,
    value,
    label,
    isPrimary,
    sortOrder: points.length,
  });
}

function parsePhonesAndFax(phoneRaw: string, faxRaw: string): ContactPoint[] {
  const points: ContactPoint[] = [];

  for (const token of splitMultiline(phoneRaw)) {
    pushContactPoint(points, 'PHONE', token, 'Mobile', points.length === 0);
  }

  for (const token of splitMultiline(faxRaw)) {
    pushContactPoint(points, 'PHONE', token, 'Fax', false);
  }

  return points;
}

function parseEmails(emailRaw: string): ContactPoint[] {
  const points: ContactPoint[] = [];
  for (const token of splitMultiline(emailRaw)) {
    const parsed = parseEmailToken(token);
    if (!parsed) continue;
    pushContactPoint(points, 'EMAIL', parsed.value, parsed.label, points.length === 0);
  }
  return points;
}

/** Contacts.xls rows tagged with C | keep the full Name value as the organization name. */
function isClientCategoryName(name: string): boolean {
  return /^'?c\s*\|/i.test(name.trim());
}

/** Category codes in Contacts.xls — left of `|` is a group, right is the organization name. */
const CATEGORY_PREFIX_PATTERN =
  /^(?:spore-|greece|malaysia-|indonesia-|hotel\s*-\s*)/i;

function isCategoryPrefix(left: string): boolean {
  const trimmed = left.trim();
  if (!trimmed) return false;
  return CATEGORY_PREFIX_PATTERN.test(trimmed);
}

function parseNameField(name: string): { clientName: string; contactPerson?: string } {
  const trimmed = name.trim();
  if (!trimmed) return { clientName: '' };

  if (isClientCategoryName(trimmed)) {
    return { clientName: trimmed };
  }

  const parts = trimmed
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return { clientName: '' };
  if (parts.length === 1) return { clientName: parts[0] };

  if (isCategoryPrefix(parts[0])) {
    if (parts.length === 2) {
      return { clientName: parts[1] };
    }
    return {
      clientName: parts[1],
      contactPerson: parts.slice(2).join(' | ') || undefined,
    };
  }

  if (parts.length === 2) {
    return {
      clientName: parts[0],
      contactPerson: parts[1] || undefined,
    };
  }

  return {
    clientName: parts[0],
    contactPerson: parts.slice(1).join(' | ') || undefined,
  };
}

function mapSheetRow(raw: Record<string, unknown>, rowIndex: number): ParsedContactsImportRow | null {
  const keyed = Object.fromEntries(
    Object.entries(raw).map(([k, v]) => [normalizeHeader(k), v]),
  );

  const sourceName = cellValue(keyed, 'name');
  if (!sourceName) return null;

  const { clientName, contactPerson } = parseNameField(sourceName);
  const emails = parseEmails(cellValue(keyed, 'e-mail', 'email', 'e mail'));
  const phones = parsePhonesAndFax(
    cellValue(keyed, 'phone', 'tel', 'telephone'),
    cellValue(keyed, 'fax'),
  );

  return {
    recordId: String(rowIndex),
    sourceName,
    clientName,
    contactPerson,
    clientAddress: cellValue(keyed, 'address') || undefined,
    clientNotes: cellValue(keyed, 'notes', 'note') || undefined,
    contactPoints: [...emails, ...phones],
  };
}

export async function parseContactsSpreadsheet(file: File): Promise<ParseContactsSpreadsheetResult> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('The spreadsheet has no worksheets.');
  }

  const sheet = workbook.Sheets[sheetName];
  const jsonRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);

  const rows: ParsedContactsImportRow[] = [];
  let skippedEmpty = 0;

  jsonRows.forEach((raw, index) => {
    const mapped = mapSheetRow(raw, index + 2);
    if (!mapped) {
      skippedEmpty += 1;
      return;
    }
    rows.push(mapped);
  });

  if (rows.length === 0) {
    throw new Error('No organization rows found. Expected columns like Name, e-mail, Phone, Address.');
  }

  return { sheetName, rows, skippedEmpty };
}
